'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import type { Publication } from '@/types/publications';

const ResearchPublications = () => {
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchPublications = async () => {
      try {
        const supabase = createClient();
        const { data, error: fetchError } = await supabase
          .from('publications')
          .select('*')
          .order('year', { ascending: false });

        if (fetchError) {
          console.error('Error fetching publications:', fetchError.message);
          setError('Unable to load publications at this time.');
          return;
        }

        setPublications(data || []);
      } catch (err: any) {
        console.error('Unexpected error:', err?.message);
        setError('Unable to load publications at this time.');
      } finally {
        setLoading(false);
      }
    };

    fetchPublications();
  }, []);

  const getPublicationUrl = (pub: Publication): string | null => {
    if (pub.doi) return `https://doi.org/${pub.doi}`;
    if (pub.link) return pub.link;
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-md shadow-organic-sm">
      {/* Collapsible Header */}
      <button
        onClick={() => setIsExpanded((prev) => !prev)}
        className="w-full flex items-center justify-between p-4 lg:p-6 text-left hover:bg-muted/30 transition-organic duration-200 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center space-x-3">
          <Icon name="AcademicCapIcon" size={20} variant="outline" className="text-primary flex-shrink-0" />
          <span className="text-sm font-body font-medium text-foreground">
            {loading
              ? 'Loading publications...'
              : `${publications.length} Publication${publications.length !== 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex items-center space-x-2 text-muted-foreground">
          <span className="text-xs font-body">{isExpanded ? 'Collapse' : 'Expand'}</span>
          <Icon
            name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
            size={18}
            variant="outline"
            className="transition-transform duration-200"
          />
        </div>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="px-4 pb-4 lg:px-6 lg:pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 text-muted-foreground">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-body">Loading publications...</span>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3 text-destructive">
                <Icon name="ExclamationCircleIcon" size={20} variant="outline" />
                <span className="text-sm font-body">{error}</span>
              </div>
            </div>
          ) : publications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-muted/50 rounded-full mb-4">
                <Icon name="AcademicCapIcon" size={32} variant="outline" className="text-muted-foreground" />
              </div>
              <p className="text-base font-body font-medium text-foreground mb-1">No publications available yet</p>
              <p className="text-sm font-body text-muted-foreground">Check back soon for scientific research and publications.</p>
            </div>
          ) : (
            <>
              <div className="space-y-4">
                {(showAll ? publications : publications.slice(0, 3)).map((pub) => {
                  const url = getPublicationUrl(pub);
                  return (
                    <div
                      key={pub.id}
                      className="bg-background border border-border rounded-md p-4 hover:shadow-organic-sm transition-organic duration-300"
                    >
                      <h4 className="text-base font-headline font-bold text-foreground mb-2">{pub.title}</h4>

                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className="text-sm font-body text-muted-foreground">
                          {pub.authors?.join(', ')}
                        </span>
                        {pub.journal && (
                          <>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-sm font-body text-primary font-medium">{pub.journal}</span>
                          </>
                        )}
                        <span className="text-muted-foreground">·</span>
                        <span className="text-sm font-body text-muted-foreground">{pub.year}</span>
                      </div>

                      {pub.abstract && (
                        <p className="text-sm font-body text-muted-foreground mb-4 line-clamp-2">{pub.abstract}</p>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-xs font-body text-muted-foreground">
                          {pub.doi && (
                            <div className="flex items-center space-x-1">
                              <Icon name="DocumentTextIcon" size={14} variant="outline" />
                              <span>DOI: {pub.doi}</span>
                            </div>
                          )}
                        </div>
                        {url && (
                          <Link
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center space-x-1 text-sm font-body font-medium text-primary hover:text-primary/80 transition-organic duration-300"
                          >
                            <span>View Publication</span>
                            <Icon name="ArrowTopRightOnSquareIcon" size={16} variant="outline" />
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {publications.length > 3 && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setShowAll((prev) => !prev)}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-body font-medium text-primary border border-primary rounded-md hover:bg-primary/10 transition-organic duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <span>{showAll ? 'Show less' : `Show all ${publications.length} publications`}</span>
                    <Icon
                      name={showAll ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={16}
                      variant="outline"
                    />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ResearchPublications;